import clsx from "clsx"
import { AnimatePresence } from "framer-motion"
import { Children, cloneElement, isValidElement, useCallback, useEffect, useRef, useState } from "react"

import { AriaLiveAnnouncer } from "@atoms/index"

import { ComponentNavigationContext, useComponentNavigation } from "./ComponentNavigation.context"
import styles from "./ComponentNavigation.module.scss"
import {
    AnimationDirectionIntent,
    AnimationDirectionValue,
    ComponentNavigationContextValues,
    ComponentNavigationProps,
    ComponentNavigationRootProps,
} from "./ComponentNavigation.types"
import ComponentNavigationComponent from "./ComponentNavigationComponent/ComponentNavigationComponent"
import { ComponentNavigationComponentProps } from "./ComponentNavigationComponent/ComponentNavigationComponent.types"
import ComponentNavigationContent from "./ComponentNavigationContent/ComponentNavigationContent"
import ComponentNavigationNavigate from "./ComponentNavigationNavigate/ComponentNavigationNavigate"

function extractComponentIdsAndPersistenceRecursively(children: React.ReactNode): {
    ids: string[]
    nonPersistent: Set<string>
} {
    const ids: string[] = []
    const nonPersistent = new Set<string>()
    Children.forEach(children, (child) => {
        if (isValidElement(child)) {
            const element = child as React.ReactElement<ComponentNavigationComponentProps>
            const props = element.props
            if (typeof props.componentId === "string") {
                ids.push(props.componentId)
                if (props.excludeFromHistory === true) {
                    nonPersistent.add(props.componentId)
                }
            }
            if (props.children) {
                const nested = extractComponentIdsAndPersistenceRecursively(props.children)
                nested.ids.forEach((id) => ids.push(id))
                nested.nonPersistent.forEach((id) => nonPersistent.add(id))
            }
        }
    })
    return { ids, nonPersistent }
}

function ComponentNavigationProvider(props: ComponentNavigationProps) {
    const {
        children,
        historyKey,
        persistToHistory = false,
        initialComponentId,
        currentComponentId: controlledComponentId,
        componentIds: explicitComponentIds,
        transitionDurationMS = 200,
        keepMounted = false,
        className,
        onNavigate,
    } = props

    const { ids: extractedComponentIds, nonPersistent: nonPersistentComponentIds } =
        extractComponentIdsAndPersistenceRecursively(children)

    const componentIds = explicitComponentIds ?? extractedComponentIds

    const isPersistentComponentId = useCallback(
        (componentId: string | null): boolean => {
            return !!componentId && !nonPersistentComponentIds.has(componentId)
        },
        [nonPersistentComponentIds],
    )

    const getComponentFromHistory = useCallback((): string | null => {
        if (!persistToHistory) {
            return null
        }
        const url = new URL(window.location.href)
        const navParam = url.searchParams.get(historyKey!)
        if (navParam && componentIds.includes(navParam) && isPersistentComponentId(navParam)) {
            return navParam
        } else {
            return null
        }
    }, [persistToHistory, historyKey, componentIds, isPersistentComponentId])

    const [internalCurrentComponentId, setInternalCurrentComponentId] = useState<string | null>(() => {
        if (controlledComponentId !== undefined) {
            return null
        }
        const historyComponentId = getComponentFromHistory()
        if (persistToHistory && historyComponentId) {
            return historyComponentId
        } else {
            return initialComponentId ? initialComponentId : (componentIds?.[0] ?? null)
        }
    })

    const currentComponentId = controlledComponentId !== undefined ? controlledComponentId : internalCurrentComponentId

    const [animationDirection, setAnimationDirection] = useState<AnimationDirectionValue>(AnimationDirectionValue.NONE)
    const [isComponentTransitioning, setIsComponentTransitioning] = useState(false)
    const [contentHeight, setContentHeight] = useState<number | undefined>(undefined)

    const detectDirection = useCallback(
        (fromComponentId: string | null, toComponentId: string): AnimationDirectionIntent => {
            if (!fromComponentId) {
                return "right"
            }
            const fromIndex = componentIds.indexOf(fromComponentId)
            const toIndex = componentIds.indexOf(toComponentId)
            if (fromIndex === -1 || toIndex === -1) {
                return "right"
            }
            return toIndex > fromIndex ? "right" : "left"
        },
        [componentIds],
    )

    const resolveAnimationDirection = useCallback(
        (
            intent: AnimationDirectionIntent,
            fromComponentId: string | null,
            toComponentId: string,
        ): AnimationDirectionValue => {
            if (intent === "auto") {
                const detected = detectDirection(fromComponentId, toComponentId)
                return detected === "right" ? AnimationDirectionValue.RIGHT : AnimationDirectionValue.LEFT
            }
            return intent === "right" ? AnimationDirectionValue.RIGHT : AnimationDirectionValue.LEFT
        },
        [detectDirection],
    )

    useEffect(() => {
        if (persistToHistory && !currentComponentId && controlledComponentId === undefined) {
            const historyComponentId = getComponentFromHistory()
            setInternalCurrentComponentId(historyComponentId || initialComponentId || null)
        }
    }, [persistToHistory, currentComponentId, getComponentFromHistory, initialComponentId, controlledComponentId])

    // Seed browser history with the initial component so that the first navigation
    // uses pushState (creating a back-navigable entry) instead of replaceState.
    const hasSeededHistory = useRef(false)
    useEffect(() => {
        if (
            persistToHistory &&
            currentComponentId &&
            isPersistentComponentId(currentComponentId) &&
            !hasSeededHistory.current
        ) {
            hasSeededHistory.current = true
            const url = new URL(window.location.href)
            const existingParam = url.searchParams.get(historyKey!)
            if (!existingParam) {
                url.searchParams.set(historyKey!, currentComponentId)
                window.history.replaceState({ componentId: currentComponentId }, "", url.toString())
            }
        }
    }, [persistToHistory, currentComponentId, historyKey, isPersistentComponentId])

    const updateBrowserHistory = useCallback(
        (componentId: string) => {
            if (persistToHistory && isPersistentComponentId(componentId)) {
                const url = new URL(window.location.href)
                const currentParam = url.searchParams.get(historyKey!)

                if (currentParam !== componentId) {
                    url.searchParams.set(historyKey!, componentId)

                    if (currentParam === null) {
                        window.history.replaceState({ componentId }, "", url.toString())
                    } else {
                        window.history.pushState({ componentId }, "", url.toString())
                    }
                }
            }
        },
        [persistToHistory, historyKey, isPersistentComponentId],
    )

    const navigate = useCallback(
        (toComponentId: string, directionIntent: AnimationDirectionIntent = "auto") => {
            setIsComponentTransitioning(true)
            const resolvedDirection = resolveAnimationDirection(directionIntent, currentComponentId, toComponentId)
            setAnimationDirection(resolvedDirection)

            if (controlledComponentId !== undefined) {
                onNavigate?.(toComponentId)
            } else {
                setInternalCurrentComponentId(toComponentId)
                updateBrowserHistory(toComponentId)
            }

            setTimeout(() => {
                setIsComponentTransitioning(false)
            }, transitionDurationMS)
        },
        [
            currentComponentId,
            resolveAnimationDirection,
            updateBrowserHistory,
            transitionDurationMS,
            controlledComponentId,
            onNavigate,
        ],
    )

    useEffect(() => {
        if (!persistToHistory) {
            return
        }

        const handlePopState = (event: PopStateEvent) => {
            const state = event.state as { componentId?: string } | null
            let nextComponentId: string | null = null
            if (
                state?.componentId &&
                componentIds.includes(state.componentId) &&
                isPersistentComponentId(state.componentId)
            ) {
                nextComponentId = state.componentId
            } else {
                const historyComponentId = getComponentFromHistory()
                if (historyComponentId && componentIds.includes(historyComponentId)) {
                    nextComponentId = historyComponentId
                } else {
                    nextComponentId = initialComponentId || null
                }
            }
            setInternalCurrentComponentId(nextComponentId)
            setAnimationDirection(AnimationDirectionValue.NONE)
        }

        window.addEventListener("popstate", handlePopState)
        return () => window.removeEventListener("popstate", handlePopState)
    }, [persistToHistory, getComponentFromHistory, initialComponentId, componentIds, isPersistentComponentId])

    const contextValue: ComponentNavigationContextValues = {
        currentComponentId,
        componentIds,
        animationDirection,
        isComponentTransitioning,
        transitionDurationMS,
        keepMounted,
        contentHeight,
        setContentHeight,
        navigate,
    }

    return (
        <ComponentNavigationContext.Provider value={contextValue}>
            <div className={clsx(className, styles.providerBase)}>{children}</div>
        </ComponentNavigationContext.Provider>
    )
}

function ComponentNavigationRoot(props: ComponentNavigationRootProps) {
    const { children, className } = props

    const { currentComponentId, contentHeight, keepMounted } = useComponentNavigation()

    return (
        <div className={clsx(styles.base, className)} style={keepMounted ? { height: "auto" } : undefined}>
            <AriaLiveAnnouncer announcement={`Component ${currentComponentId} is now active`} />
            <div
                className={styles.componentContainer}
                // In keepMounted mode, the active step sizes to its content and the surrounding
                // scroll container (e.g. a popover body) handles overflow + the keyboard-aware
                // viewport bound, so drop the SCSS viewport max-height that would otherwise clip
                // steps taller than the viewport. The animated (non-keepMounted) mode keeps it.
                style={keepMounted ? { height: "auto", maxHeight: "none" } : { height: contentHeight }}
            >
                {keepMounted ? (
                    children
                ) : (
                    <AnimatePresence mode="sync" initial={false}>
                        {Children.map(children, (child) => {
                            if (isValidElement(child) && "componentId" in child.props) {
                                const componentProps = child.props as ComponentNavigationComponentProps
                                if (componentProps.componentId === currentComponentId) {
                                    return cloneElement(child, { key: currentComponentId })
                                }
                            } else {
                                return child
                            }
                        })}
                    </AnimatePresence>
                )}
            </div>
        </div>
    )
}

const Provider = ComponentNavigationProvider
const Root = ComponentNavigationRoot
const Component = ComponentNavigationComponent
const Content = ComponentNavigationContent
const Navigate = ComponentNavigationNavigate

export default {
    Provider,
    Root,
    Component,
    Content,
    Navigate,
}
